// Terrain Advisor layer family — hillshade + native MapLibre
// `color-relief` TAWS color ramp.
//
// Two sources / two layers:
//
//   - `terrain-dem` (raster-dem) + `terrain-hillshade` layer  →
//     neutral grey hillshade relief (no color, just shading).
//
//   - `terrain-dem-color` (raster-dem) + `terrain-color` layer →
//     native MapLibre 5 `color-relief` layer that GPU-shades the DEM
//     with a ["interpolate", ["linear"], ["elevation"], ...] color
//     ramp keyed by elevation in meters. The actual ramp stops are
//     computed by the visibility/altitude effect in MapView; this
//     module only mounts an all-transparent default ramp that the
//     effect's first run rewrites for the current altitude.
//
// Why two raster-dem sources for the same tiles: MapLibre warns when
// one source is used for both 3D terrain and color-relief (we don't
// enable 3D, but the warning fires anyway when hillshade +
// color-relief both read from one DEM source). Two sources, same
// upstream tiles, no warning.
//
// Coupling with MapView: the existing `terrain` `registerStyleRebuild`
// entry in MapView calls `applyTerrainRef.current()` to re-apply the
// altitude-driven ramp + visibility after every basemap rebuild. That
// stays. This module only owns the mount() — the registry entry it
// installs here is a no-op-on-restore wrapper since MapView's own
// `terrain` entry handles applyState. Registering the same id twice
// is fine; the Map<id, entry> dedup means MapView's entry is the
// authoritative restore.
//
// Extracted from addOverlays.ts as HI-1 batch 6b (audit 2026-05-22).
// Pure mechanical move — source spec, paint, layer order all
// byte-identical to the pre-extraction form.

import type { Map as MlMap } from 'maplibre-gl';
import { registerLayerFamily } from '../layerVisibilityRegistry';

/**
 * Mount the terrain hillshade + color-relief sources and layers.
 * Idempotent on each source.
 */
export function addTerrainLayers(map: MlMap): void {
  // ---- Hillshade (neutral grey relief texture) ---------------------
  if (!map.getSource('terrain-dem')) {
    map.addSource('terrain-dem', {
      type: 'raster-dem',
      tiles: ['/api/wx/terrain?z={z}&x={x}&y={y}'],
      encoding: 'terrarium',
      tileSize: 256,
      attribution: 'Terrain: AWS Open Data Terrarium DEM',
      maxzoom: 12,
    } as any);
    const beforeId = (() => {
      for (const id of ['sua-fill', 'tfr-fill', 'fires-fill', 'sfcanalysis-raster', 'satgeocolor-raster', 'satir-raster', 'nexrad-raster']) {
        if (map.getLayer(id)) return id;
      }
      return undefined;
    })();
    map.addLayer(
      {
        id: 'terrain-hillshade',
        type: 'hillshade',
        source: 'terrain-dem',
        layout: { visibility: 'none' },
        paint: {
          // Neutral grey palette — relief texture only. The colored
          // TAWS overlay (terrain-color layer) stacks on top with
          // red/amber for hazardous terrain. Reserving those colors
          // for actual safety state was Wiley's explicit ask and
          // matches Garmin / ForeFlight convention.
          'hillshade-shadow-color': '#000000',
          'hillshade-highlight-color': '#ffffff',
          'hillshade-accent-color': '#000000',
          'hillshade-exaggeration': 0.45,
          'hillshade-illumination-anchor': 'map',
        },
      },
      beforeId,
    );
  }

  // ---- Terrain Advisor v4: native MapLibre `color-relief` layer ----
  // MapLibre 5 has a native `color-relief` layer type that GPU-shades
  // a raster-dem source with a ["interpolate", ["linear"],
  // ["elevation"], ...] color-ramp expression keyed by elevation in
  // meters. Same shader path as the built-in hillshade — fully
  // offline once the DEM tiles are cached, and re-rendering at a new
  // altitude is a paint-property update, not a tile fetch.
  //
  // This replaces the server-side colored-PNG approach (which worked
  // but couldn't render new altitudes offline). The actual color
  // stops are computed in the visibility/altitude effect in MapView.
  if (!map.getSource('terrain-dem-color')) {
    // Separate source from terrain-dem to avoid the MapLibre warning
    // about sharing one source for both 3D terrain (we don't enable
    // 3D, but the warning fires anyway when hillshade + color-relief
    // both read from terrain-dem). Same upstream tiles.
    map.addSource('terrain-dem-color', {
      type: 'raster-dem',
      tiles: ['/api/wx/terrain?z={z}&x={x}&y={y}'],
      encoding: 'terrarium',
      tileSize: 256,
      attribution: 'Terrain: AWS Open Data Terrarium DEM',
      maxzoom: 12,
    } as any);
    const tawsBeforeId = (() => {
      for (const id of ['sua-fill', 'tfr-fill', 'fires-fill']) {
        if (map.getLayer(id)) return id;
      }
      return undefined;
    })();
    map.addLayer(
      {
        id: 'terrain-color',
        type: 'color-relief',
        source: 'terrain-dem-color',
        layout: { visibility: 'none' },
        paint: {
          // 90% so a hint of the underlying chart shows through.
          'color-relief-opacity': 0.9,
          // Default color ramp — all transparent until the effect
          // in MapView rewrites it for the current altitude.
          'color-relief-color': [
            'interpolate', ['linear'], ['elevation'],
            0,    'rgba(0,0,0,0)',
            9999, 'rgba(0,0,0,0)',
          ],
        },
      } as any,
      tawsBeforeId,
    );
  }
}

// NOTE: we deliberately register under a DIFFERENT id (`terrain-mount`)
// than MapView's existing `terrain` registry entry. MapView's `terrain`
// entry calls applyTerrainRef.current() which re-applies the
// altitude-driven ramp + visibility after a basemap rebuild AND calls
// apply() which itself depends on the layers existing. If we registered
// under `terrain`, our mount() would replace MapView's restore in the
// registry Map and the altitude ramp would never re-apply.
//
// With a separate id, both restores run on basemap rebuild: this module
// re-mounts the sources/layers; MapView's `terrain` entry then re-runs
// the altitude ramp. Ordering between two registry entries isn't
// guaranteed, but MapView's apply() is internally guarded by
// `map.getLayer('terrain-hillshade')` / `map.getLayer('terrain-color')`
// checks, so a no-op-then-mount-then-rerun sequence is also safe.
registerLayerFamily({
  id: 'terrain-mount',
  label: 'Terrain DEM mount',
  sourceId: 'terrain-dem',
  layerIds: ['terrain-hillshade', 'terrain-color'],
  mount: (map) => addTerrainLayers(map),
});
